export type RaceSound = 'start' | 'select' | 'correct' | 'wrong' | 'timeout' | 'turbo' | 'overtake' | 'finish';

export class RaceAudio {
  private context?: AudioContext;
  private master?: GainNode;
  private engine?: OscillatorNode;
  private engineGain?: GainNode;
  private muted = false;

  unlock() {
    if (!this.context) {
      const Context = window.AudioContext
        ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Context) return;
      this.context = new Context();
      this.master = this.context.createGain();
      this.master.gain.value = this.muted ? 0 : 0.16;
      this.master.connect(this.context.destination);
    }
    if (this.context.state === 'suspended') void this.context.resume();
  }

  toggleMuted() {
    this.muted = !this.muted;
    if (this.master && this.context) this.master.gain.setTargetAtTime(this.muted ? 0 : 0.16, this.context.currentTime, 0.03);
    return this.muted;
  }

  startEngine() {
    this.unlock();
    if (!this.context || !this.master || this.engine) return;
    this.engine = this.context.createOscillator();
    this.engineGain = this.context.createGain();
    this.engine.type = 'sawtooth';
    this.engine.frequency.value = 58;
    this.engineGain.gain.value = 0.025;
    this.engine.connect(this.engineGain).connect(this.master);
    this.engine.start();
  }

  setEngineLoad(load: number) {
    if (!this.context || !this.engine) return;
    this.engine.frequency.setTargetAtTime(55 + Math.max(0, Math.min(1, load)) * 78, this.context.currentTime, 0.07);
  }

  play(sound: RaceSound) {
    this.unlock();
    const context = this.context;
    const master = this.master;
    if (!context || !master || context.state !== 'running') return;
    const notes: Record<RaceSound, readonly number[]> = {
      start: [196, 294, 392], select: [520], correct: [440, 659, 880], wrong: [210, 155],
      timeout: [250, 190, 130], turbo: [180, 320, 640, 960], overtake: [360, 480, 720],
      finish: [392, 523, 659, 784, 1047],
    };
    notes[sound].forEach((frequency, index) => {
      const at = context.currentTime + index * 0.065;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = sound === 'wrong' || sound === 'timeout' ? 'sawtooth' : sound === 'turbo' ? 'square' : 'triangle';
      oscillator.frequency.setValueAtTime(frequency, at);
      if (sound === 'turbo') oscillator.frequency.exponentialRampToValueAtTime(frequency * 1.8, at + 0.18);
      gain.gain.setValueAtTime(sound === 'turbo' ? 0.055 : 0.1, at);
      gain.gain.exponentialRampToValueAtTime(0.001, at + (sound === 'finish' ? 0.32 : 0.2));
      oscillator.connect(gain).connect(master);
      oscillator.start(at);
      oscillator.stop(at + 0.36);
    });
  }

  destroy() {
    this.engine?.stop();
    this.engine = undefined;
    void this.context?.close();
    this.context = undefined;
    this.master = undefined;
    this.engineGain = undefined;
  }
}
